import { z } from 'zod';
import { COSMETICS } from '../../content/cosmetics';
import { MOWER_IDS, type MowerId } from '../../content/mowers';
import { RACE_SIZES } from '../../gen/modes';
import { PlayerIdSchema, PlayerNameSchema, PostedRunSchema } from './schema';

/**
 * The live room's messages (docs/LIVE.md, "The room"): JSON over a WebSocket, checked with these
 * schemas on both sides. Times are milliseconds on the server's clock; ticks are the sim's.
 */

const int = () => z.number().int();
/** A seat in a room: 0 the inviter; a party (docs/PARTY.md) has up to four. */
const SeatSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
/** A per-seat list in a match's result: one for each player in it (two to four). */
const PerSeat = () => z.array(int()).min(2).max(4);
const TickSchema = int()
  .min(0)
  .max(30 * 60 * 60);
const SizeSchema = z.enum(RACE_SIZES);
/** A racer's turn number this race (0 first): a resent turn is known by it. */
const TurnNumberSchema = int().min(0).max(5000);

/** Room codes: 6 characters from an alphabet without look-alikes (no 0/O, 1/I/L). */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const RoomCodeSchema = z.string().regex(/^[2-9A-HJKMNP-Z]{6}$/);

/**
 * What a room plays: a race on twin lawns (docs/LIVE.md), head to head on one lawn
 * (docs/HEAD_TO_HEAD.md), or one of its spin-offs: mower soccer (docs/SOCCER_MATCH.md), the
 * sheep round-up (docs/ROUNDUP_MATCH.md) and the hedge maze (docs/MAZE_MATCH.md).
 */
export const ROOM_MODES = ['race', 'duel', 'soccer', 'roundup', 'maze'] as const;
export type RoomMode = (typeof ROOM_MODES)[number];

/**
 * Whether a room plays one shared lawn, judged by replaying the match (head to head and its
 * spin-offs), rather than a race on twin lawns.
 */
export function isMatchMode(mode: RoomMode | null | undefined): boolean {
  return mode === 'duel' || mode === 'soccer' || mode === 'roundup' || mode === 'maze';
}

/** What an invite to each mode says after the sender's name ("DEWY WREN 12 wants a race!"). */
export const MODE_WANTS: Record<RoomMode, string> = {
  race: 'wants a race!',
  duel: 'wants to go head to head!',
  soccer: 'wants a soccer match!',
  roundup: 'wants a sheep round-up!',
  maze: 'wants a maze race!',
};

/** What a game too old for a room's mode is told it needs ("update the game to ..."). */
export const MODE_NEEDS: Record<RoomMode, string> = {
  race: 'race',
  duel: 'play head to head',
  soccer: 'play soccer',
  roundup: 'play the round-up',
  maze: 'play the maze',
};

/** POST /rooms: a new room for a race or a match of this size. */
/** How many a room seats: two, or a party of up to four (docs/PARTY.md). */
export const ROOM_SEATS = [2, 4] as const;
export type RoomSeats = (typeof ROOM_SEATS)[number];

/** Whether a mode can be played by a party of up to four (races on twin lawns stay one on one). */
export function partyMode(mode: RoomMode): boolean {
  return isMatchMode(mode);
}

export const CreateRoomSchema = z.strictObject({
  mode: z.enum(ROOM_MODES),
  size: SizeSchema,
  /** A party room (docs/PARTY.md): up to four. Absent: two. */
  seats: z.union([z.literal(2), z.literal(4)]).optional(),
});

/**
 * Head to head: how far behind the room's clock a turn may be stamped (half a second). An older
 * one is moved up to this; so a match's turns up to this far back are final, and the room judges
 * the match from them (docs/HEAD_TO_HEAD.md).
 */
export const MAX_LATE_TICKS = 30;
/** Head to head: how far ahead of the room's clock a turn may be (a phone's clock running fast). */
export const MAX_AHEAD_TICKS = 300;
/** The new room's code and the link to share. */
export const RoomCreatedSchema = z.object({ code: RoomCodeSchema, url: z.string() });
/** POST /rooms/:code/ticket: a one-use ticket for the WebSocket, valid for a minute. */
export const RoomTicketSchema = z.object({ ticket: z.string().min(16).max(64) });

/**
 * A player's paint on each mower (a cosmetic, nothing more): head to head shows the friend's mower
 * in its owner's paint. Sent loosely and kept only where it names a mower and a paint this build
 * knows ({@link knownPaints}), so a newer build's paint never breaks an older one's messages.
 */
export type MowerPaints = Partial<Record<MowerId, string>>;
const LoosePaintsSchema = z.record(z.string().max(32), z.string().max(48));
const PAINT_IDS = new Set(COSMETICS.filter((c) => c.slot === 'paint').map((c) => c.id));

/** Only the entries naming a known mower and a known paint. */
export function knownPaints(raw: Readonly<Record<string, string>> | undefined): MowerPaints {
  const out: MowerPaints = {};
  for (const id of MOWER_IDS) {
    const paint = raw?.[id];
    if (paint && PAINT_IDS.has(paint)) out[id] = paint;
  }
  return out;
}

// ── From a phone ─────────────────────────────────────────────────────────────────────────────

export const ClientMessageSchema = z.discriminatedUnion('type', [
  /** Ready in the room (again after a race, for a rematch), with this player's paints. */
  z.strictObject({ type: z.literal('ready'), paints: LoosePaintsSchema.optional() }),
  /** The inviter starts the race once both are ready. */
  z.strictObject({ type: z.literal('start') }),
  /**
   * A turn, as a run carries it: `[tick, intent code]`, and its number this race (`n`). After a
   * reconnect every turn is sent again; the friend's phone keeps each number once.
   */
  z.strictObject({
    type: z.literal('input'),
    t: TickSchema,
    c: int().min(0).max(255),
    n: TurnNumberSchema,
  }),
  /**
   * Where the sim is, four times a second: the tick, the share mowed, the state's hash, and how
   * many turns came before it (`n`), so the friend's ghost waits for any still on their way.
   */
  z.strictObject({
    type: z.literal('progress'),
    t: TickSchema,
    pct: int().min(0).max(100),
    hash: z.string().max(16),
    n: TurnNumberSchema,
  }),
  /** The race is over for this player: the whole run, to be checked. */
  z.strictObject({ type: z.literal('finish'), run: PostedRunSchema }),
  z.strictObject({ type: z.literal('rematch') }),
  z.strictObject({ type: z.literal('leave') }),
  /** For the clock offset: the phone's clock when sent. */
  z.strictObject({ type: z.literal('ping'), at: z.number() }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ── From the room ────────────────────────────────────────────────────────────────────────────

const RoomPlayerSchema = z.object({
  seat: SeatSchema,
  player: PlayerIdSchema,
  name: PlayerNameSchema,
  ready: z.boolean(),
  /** Connected now (a racer away for a moment shows as not). */
  here: z.boolean(),
  /** Out of this race (left, or away past the grace): told at once, not only in the result. */
  out: z.boolean(),
  /** The player's paint on each mower (head to head shows it); read with `knownPaints`. */
  paints: LoosePaintsSchema.optional(),
});

/** How a racer's race ended, as the room judged it. */
const RaceEndSchema = z.enum(['cleared', 'stopped', 'forfeit', 'refused']);

export const ServerMessageSchema = z.discriminatedUnion('type', [
  /** Who is in the room and its state; sent on every change. `you`: this socket's seat. */
  z.object({
    type: z.literal('room'),
    code: RoomCodeSchema,
    size: SizeSchema,
    you: SeatSchema,
    players: z.array(RoomPlayerSchema).max(4),
    phase: z.enum(['waiting', 'countdown', 'racing', 'done']),
    /** What the room plays (absent from rooms opened before head to head: a race). */
    mode: z.enum(ROOM_MODES).optional(),
    /**
     * The inviter's rules version: a match plays by its sim rules (`simRulesFor`). Absent from
     * rooms before version 9 (they played by version 8's).
     */
    rules: int().min(1).max(1000).optional(),
    /** How many the room seats (absent: two). */
    seats: z.union([z.literal(2), z.literal(4)]).optional(),
  }),
  /**
   * The race starts at `at` on the lawn made from `seed`. `server` is the server's clock as it sent
   * this, so a phone places the start on its own clock without trusting a clock estimate: arrival
   * time plus (`at` - `server`), off only by the message's trip.
   */
  z.object({
    type: z.literal('countdown'),
    at: z.number(),
    server: z.number(),
    seed: z.string().min(1).max(64),
  }),
  /**
   * The other racer's turn. Head to head: every turn, both seats' (a phone's own too), with the
   * tick the room settled on, which is final.
   */
  z.object({
    type: z.literal('input'),
    seat: SeatSchema,
    t: TickSchema,
    c: int(),
    n: TurnNumberSchema,
  }),
  /** The other racer's progress. */
  z.object({
    type: z.literal('progress'),
    seat: SeatSchema,
    t: TickSchema,
    pct: int().min(0).max(100),
    hash: z.string().max(16),
    n: TurnNumberSchema,
  }),
  /**
   * The result: the winner (null: a draw), how each seat's race ended, its ticks, and who took
   * each coin (by the lawn's coin order; null: nobody).
   */
  z.object({
    type: z.literal('result'),
    winner: SeatSchema.nullable(),
    ends: z.tuple([RaceEndSchema, RaceEndSchema]),
    ticks: z.tuple([int().nullable(), int().nullable()]),
    coins: z.array(SeatSchema.nullable()).max(32),
  }),
  /**
   * Head to head's result, from the room's own replay of the match: the winner (null: a draw),
   * why it ended (`forfeit`: a player left or was away too long), the grass and coins each took,
   * and the tick it ended on.
   */
  z.object({
    type: z.literal('match'),
    winner: SeatSchema.nullable(),
    why: z.enum(['half', 'done', 'brokeDown', 'forfeit', 'goals', 'time', 'sheep', 'maze', 'lead']),
    tiles: PerSeat(),
    coins: PerSeat(),
    tick: int(),
    /** A soccer match: each seat's goals. */
    goals: PerSeat().optional(),
    /** A round-up: the sheep in each seat's pen. */
    penned: PerSeat().optional(),
    /** A maze: each seat's tiles from the middle along the paths, as it ended. */
    distance: PerSeat().optional(),
    /**
     * A party (docs/PARTY.md): each seat's place, 1 first (the same score shares a place; a
     * player who left or went silent is last). Absent for two: the winner is first.
     */
    places: z.array(int().min(1).max(4)).min(2).max(4).optional(),
  }),
  /** A racer left the room for good. */
  z.object({ type: z.literal('left'), seat: SeatSchema }),
  /** The answer to a ping: the phone's time echoed, and the server's. */
  z.object({ type: z.literal('pong'), at: z.number(), server: z.number() }),
  z.object({ type: z.literal('error'), problem: z.string().max(200) }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/**
 * The phone's clock offset from the server's (add it to the phone's time to get the server's),
 * from ping round trips: the one with the shortest round trip, assuming the halves are equal.
 */
export function clockOffset(
  samples: readonly { sent: number; got: number; server: number }[],
): number {
  let best: (typeof samples)[number] | undefined;
  for (const s of samples) if (!best || s.got - s.sent < best.got - best.sent) best = s;
  if (!best) return 0;
  return best.server - (best.sent + best.got) / 2;
}
